<template>
  <div class="sbs-matrix" :style="{ '--sbs-title-w': titleWidth + 'px' }">
    <div class="sbs-toolbar">
      <div class="sbs-toolbar__filters">
        <el-select v-model="periodView" style="width: 180px" @change="onPeriodViewSelect">
          <el-option v-for="opt in periodViewOptions" :key="opt.value" :label="opt.label" :value="opt.value">
            <span class="sbs-period-option">
              <span class="sbs-period-dot" :class="{ 'is-on': opt.hasData }"
                :style="opt.hasData && opt.color ? { background: opt.color } : null" />
              {{ opt.label }}
            </span>
          </el-option>
        </el-select>
        <el-input v-model="search" placeholder="Qidirish..." clearable style="width: 200px" @input="onSearchInput"
          @clear="fetchMatrix">
          <template #prefix>🔎</template>
        </el-input>
        <el-select v-model="rootId" placeholder="Ko'rsatkich: barchasi" clearable filterable style="width: 220px"
          @change="fetchMatrix">
          <el-option v-for="o in rootOptions" :key="o.id" :label="o.label" :value="o.id" />
        </el-select>
        <el-checkbox v-model="hideEmptyRows" class="sbs-hide-empty">Faqat to'ldirilganlar</el-checkbox>
      </div>
      <div class="sbs-toolbar__actions">
        <el-button :disabled="!columns.length" @click="copyPeriodDialogVisible = true">
          Chorakni nusxalash
        </el-button>
        <el-button :disabled="!columns.length" @click="exportCsv">⤓ Excel</el-button>
        <span v-if="saving" class="sbs-save-progress">{{ saveProgress.done }}/{{ saveProgress.total }} so'rov
          yuborildi</span>
        <el-button type="primary" :disabled="!dirtyCount || saving" :loading="saving" @click="saveAll">
          💾 Saqlash<span v-if="dirtyCount"> ({{ dirtyCount }})</span>
        </el-button>
      </div>
    </div>

    <div class="sbs-period-banner" :style="{ '--sbs-q-color': bannerColor }">
      <span class="sbs-period-banner__label">Tahrirlanmoqda</span>
      <span class="sbs-period-banner__value">{{ bannerLabel }}</span>
      <span v-if="!yearHasAnyData" class="sbs-period-banner__note">bu yil hali to'ldirilmagan</span>
      <span v-if="dirtyCount" class="sbs-period-banner__dirty">{{ dirtyCount }} ta katak saqlanmagan</span>
    </div>

    <el-skeleton v-if="loading" :rows="8" animated />
    <div v-else-if="!columns.length" class="sbs-setup">
      <h3 class="sbs-setup__title">{{ year }}-yil jadvali hali tayyor emas</h3>
      <p class="sbs-setup__text">
        Jadval to'ldirish uchun avval ko'rsatkichlar daraxti, so'ng shu yilga biriktirilgan
        ustunlar kerak.
      </p>
      <ol class="sbs-setup__steps">
        <li>
          <b>Ko'rsatkichlar</b> — jadval qatorlari (hamma yillar uchun umumiy daraxt)
          <el-button size="small" @click="$emit('go-tab', 'rows')">Ochish</el-button>
        </li>
        <li>
          <b>Ustunlar</b> — aynan {{ year }}-yilga biriktiriladi
          <el-button size="small" type="primary" @click="$emit('go-tab', 'columns')">Ochish</el-button>
        </li>
        <li>Shu yerga qaytib, har chorak ustuniga qiymatlarni kiriting</li>
      </ol>
    </div>
    <template v-else>
      <div ref="gridWrap" class="sbs-grid-wrap" tabindex="0" @keydown="onKeydown" @paste="onPaste" @copy="onCopy">
        <table class="sbs-grid">
          <thead>
            <tr>
              <th class="sbs-c-idx sbs-th-corner" :rowspan="headerDepth" :style="headerTop(0)">№</th>
              <th class="sbs-c-title sbs-th-corner" :rowspan="headerDepth" :style="headerTop(0)">
                <div class="sbs-title-clamp">Ko'rsatkich</div>
                <span class="sbs-title-resizer" title="Ustun kengligini sudrang (2 marta bosilsa — standart)"
                  @mousedown.prevent="startTitleResize" @dblclick="resetTitleWidth" />
              </th>
              <th v-for="cell in headerRows.top" :key="cell.key" :colspan="cell.colspan" :rowspan="cell.rowspan"
                :class="headerThClass(cell)" :style="headerTop(0)">
                <template v-if="cell.kind === 'group'">{{ cell.label }}</template>
                <template v-else>
                  <div class="sbs-th-title-wrap">
                    <span>{{ displayLangText(cell.col.title) }}</span>
                    <span class="sbs-mode-chip" :class="'sbs-chip--' + cell.col.value_type.toLowerCase()">
                      {{ modeChipLabel(cell.col) }}
                    </span>
                  </div>
                  <span v-if="cell.col.is_active === false" class="sbs-inactive-ico"
                    title="Portalda ko'rinmaydi">🚫</span>
                </template>
              </th>
            </tr>
            <tr>
              <th v-for="cell in headerRows.second" :key="cell.key" :colspan="cell.colspan"
                :class="headerThClass(cell)" :style="headerTop(1)">
                <div class="sbs-th-title-wrap">
                  <span>{{ displayLangText(cell.col.title) }}</span>
                  <span class="sbs-mode-chip" :class="'sbs-chip--' + cell.col.value_type.toLowerCase()">
                    {{ modeChipLabel(cell.col) }}
                  </span>
                </div>
                <span v-if="cell.col.is_active === false" class="sbs-inactive-ico" title="Portalda ko'rinmaydi">🚫</span>
              </th>
            </tr>
            <tr v-if="isMultiQuarter">
              <th v-for="cell in headerRows.third" :key="cell.key" class="sbs-th-quarter"
                :class="{ 'is-inactive': cell.col.is_active === false, 'is-q-start': cell.first,
                          'is-col-active': focusedGridColumn && focusedGridColumn.key === cell.gcKey }"
                :style="{ '--sbs-q-color': quarterColor(cell.quarter), ...headerTop(2) }">
                {{ quarterShort(cell.quarter) }}
                <span v-if="quarterHasData(cell.quarter)" class="sbs-th-q-dot" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!visibleRows.length">
              <td :colspan="gridColumns.length + 2" class="sbs-empty-row">Ko'rsatkichlar topilmadi</td>
            </tr>
            <tr v-for="(vr, rIdx) in visibleRows" :key="vr.row.id" :class="rowTrClass(vr, rIdx)">
              <td class="sbs-c-idx sbs-td-idx">
                <span v-if="vr.row.has_children" class="sbs-expand" @click="toggleExpand(vr.row.id)">{{
                  expandedIds.has(vr.row.id)
                  ? "▾" : "▸" }}</span>
                <span class="sbs-row-number">{{ layerNumbers.get(vr.row.id) }}</span>
              </td>
              <td class="sbs-c-title sbs-td-title">
                <div class="sbs-title-clamp" :style="{ paddingLeft: vr.level * 16 + 'px' }"
                  :title="displayLangText(vr.row.title)">
                  <span :class="{ 'is-bold': vr.row.is_bold }">{{ displayLangText(vr.row.title) || "..." }}</span>
                  <sup v-if="displayLangText(vr.row.note)" class="sbs-row-note">{{ displayLangText(vr.row.note) }}</sup>
                  <span v-if="vr.row.is_active === false" class="sbs-inactive-ico"
                    title="Portalda ko'rinmaydi">🚫</span>
                </div>
              </td>
              <td v-for="(gc, cIdx) in gridColumns" :id="'sbs-cell-' + rIdx + '-' + cIdx" :key="gc.key"
                class="sbs-td-cell" :class="cellClass(rIdx, cIdx, vr.row, gc)" @click="onCellClick(rIdx, cIdx)"
                @dblclick="onCellDblClick(rIdx, cIdx)">
                <input v-if="isEditing(rIdx, cIdx)" ref="editInput" v-model="editBuffer" class="sbs-cell-input"
                  :class="{ 'is-error': editError }" @keydown="onEditKeydown($event, rIdx, cIdx)"
                  @blur="commitEdit(rIdx, cIdx, {})" />
                <span v-else class="sbs-cell-value">
                  <span class="sbs-cell-primary" :class="{ 'is-empty': cellParts(vr.row, gc).isEmpty }">
                    {{ cellParts(vr.row, gc).primary }}
                  </span>
                  <span v-if="cellParts(vr.row, gc).secondary" class="sbs-cell-secondary">
                    {{ cellParts(vr.row, gc).secondary }}
                  </span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="focusedPreview" class="sbs-preview-bar">
        <span class="sbs-preview-label">
          {{ focusedPreview.rowTitle }} · {{ focusedPreview.colTitle }} · {{ focusedPreview.quarterLabel }}:
        </span>
        <template v-if="focusedPreview.num === null || focusedPreview.num === undefined || isNaN(focusedPreview.num)">
          <span class="sbs-muted">—</span>
        </template>
        <template v-else>
          <span class="sbs-preview-raw">
            <span>{{ razryadHead(focusedPreview.num, focusedPreview.scale) }}</span><span class="sbs-preview-tail">{{
              razryadTail(focusedPreview.num, focusedPreview.scale) }}</span>
            so'm
          </span>
          <span class="sbs-preview-arrow">→</span>
          <span class="sbs-preview-scaled">
            {{ formatScaled(focusedPreview.num, focusedPreview.scale, focusedPreview.decimals) }} {{ focusedPreview.unit
            }}
          </span>
        </template>
      </div>

      <p class="sbs-kbd-hint">
        ↑↓←→ ko'chish · Tab keyingi katak · Enter tahrirlash/tasdiqlash · Esc bekor qilish ·
        Delete tozalash · Ctrl+C/V nusxalash/qo'yish · Ctrl+Z bekor qilish · Ctrl+S saqlash ·
        "Ko'rsatkich" ustuni chegarasini sudrab kengaytiring
      </p>
    </template>

    <SubsidiesCopyPeriodDialog v-model="copyPeriodDialogVisible" :periods="periods" :current-year="year"
      :current-quarter="copySourceQuarter" :source-column-ids="columnIds" @copied="onPeriodCopied" />
  </div>
</template>

<script>
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import SubsidiesCopyPeriodDialog from "./SubsidiesCopyPeriodDialog.vue";
import {
  API_BASE, displayLangText, scaleUnitLabel, formatScaled, groupThousands, splitRazryad,
  parseNumericInput, parseAmountWithUnit, MAX_SAFE_AMOUNT, sortTreeByOrder, buildLayerNumbers,
  QUARTER_OPTIONS, QUARTER_ONLY_OPTIONS, normalizeQuarter, quarterOptionsForYear, pickDefaultQuarter,
  periodHasData, periodLabel, quarterColor, quarterLabel, periodFileSuffix,
} from "./subsidiesTypes";

// Chorak dropdown'ining "hammasi" rejimi — ustun ostida I..IV yonma-yon chiqadi.
const ALL_QUARTERS = "all";

// "Ko'rsatkich" ustuni: nomlar juda uzun bo'lgani uchun kengligi qat'iy belgilanadi
// (aks holda jadval qiymat ustunlarini ekrandan chiqarib yuboradi) va foydalanuvchi
// uni sarlavha chegarasidan sudrab o'zgartira oladi.
const TITLE_WIDTH_KEY = "obSubsidiesTitleWidth";
const TITLE_WIDTH_DEFAULT = 320;
const TITLE_WIDTH_MIN = 160;
const TITLE_WIDTH_MAX = 720;

function storedTitleWidth() {
  try {
    const saved = parseInt(localStorage.getItem(TITLE_WIDTH_KEY), 10);
    if (saved >= TITLE_WIDTH_MIN && saved <= TITLE_WIDTH_MAX) return saved;
  } catch (e) {
    // localStorage yopiq bo'lsa — standart kenglik
  }
  return TITLE_WIDTH_DEFAULT;
}

export default {
  name: "SubsidiesMatrix",
  components: { SubsidiesCopyPeriodDialog },
  props: {
    // Yil kontekstini SubsidiesIndex boshqaradi — grid faqat davr ko'rinishini almashtiradi.
    year: { type: Number, required: true },
    periods: { type: Array, default: () => [] },
  },
  emits: ["go-tab", "saved"],
  data() {
    return {
      periodView: ALL_QUARTERS,
      // Ekranda turgan (yuklangan) davr — dropdown bekor qilinganda shunga qaytariladi
      // va saqlash/eksport aynan shunga bog'lanadi.
      loadedYear: null,
      loadedView: ALL_QUARTERS,
      hideEmptyRows: false,
      titleWidth: storedTitleWidth(),
      titleResizeCleanup: null,
      search: "",
      searchTimer: null,
      rootId: null,
      rootOptions: [],
      // Sarlavha qatorlarining sticky ofsetlari — 3 qavat ustma-ust tushmasligi uchun
      // render'dan keyin o'lchanadi.
      headerOffsets: [],
      loading: false,
      saving: false,
      saveProgress: { done: 0, total: 0 },
      columns: [],
      rowsTree: [],
      // { [quarter]: Map(rowId -> Map(columnId -> {num, text})) }
      cellIndex: {},
      expandedIds: new Set(),
      focusedCell: null,
      editingCell: null,
      editBuffer: "",
      editError: false,
      // Kalit: `rowId:columnId:quarter`
      pendingChanges: {},
      history: [],
      historyIndex: -1,
      copyPeriodDialogVisible: false,
    };
  },
  computed: {
    isMultiQuarter() {
      return this.periodView === ALL_QUARTERS;
    },
    // Ekranda ochiq turgan choraklar — ko'p rejimda I..IV, aks holda bittasi.
    activeQuarters() {
      if (this.isMultiQuarter) return QUARTER_ONLY_OPTIONS.map((q) => q.value);
      return [normalizeQuarter(this.periodView)];
    },
    periodViewOptions() {
      const single = quarterOptionsForYear(this.periods, this.year).map((q) => ({
        value: q.value,
        label: q.label,
        color: q.color,
        hasData: periodHasData(this.periods, this.year, q.value),
      }));
      return [
        { value: ALL_QUARTERS, label: "Barcha choraklar (I–IV)", color: null, hasData: this.yearHasAnyData },
        ...single,
      ];
    },
    headerDepth() {
      return this.isMultiQuarter ? 3 : 2;
    },
    yearHasAnyData() {
      const period = this.periods.find((p) => p.year === this.year);
      return !!(period && (period.quarters.length || period.has_annual));
    },
    bannerLabel() {
      if (!this.isMultiQuarter) return periodLabel(this.year, this.periodView);
      return `${this.year}-yil · I–IV choraklar`;
    },
    bannerColor() {
      return this.isMultiQuarter ? "#0a194e" : quarterColor(this.periodView);
    },
    // Nusxalash oynasi uchun manba chorak: bitta chorak rejimida o'sha, aks holda
    // ma'lumoti bor oxirgi chorak.
    copySourceQuarter() {
      return this.isMultiQuarter ? pickDefaultQuarter(this.periods, this.year) : normalizeQuarter(this.periodView);
    },
    columnIds() {
      return this.columns.map((c) => c.id);
    },
    focusedGridColumn() {
      return this.gridColumns[this.focusedCell?.colIndex ?? -1] || null;
    },
    // Gridning haqiqiy kataklari: har ustun × har ochiq chorak.
    gridColumns() {
      const acc = [];
      this.columns.forEach((col) => {
        this.activeQuarters.forEach((quarter, qIdx) => {
          acc.push({ key: `${col.id}:${quarter}`, col, quarter, first: qIdx === 0 });
        });
      });
      return acc;
    },
    headerSegments() {
      const segments = [];
      let i = 0;
      while (i < this.columns.length) {
        const col = this.columns[i];
        if (col.group_code) {
          let j = i + 1;
          while (j < this.columns.length && this.columns[j].group_code === col.group_code) j++;
          segments.push({
            key: col.id,
            merged: true,
            groupLabel: displayLangText(col.group) || col.group_code,
            cols: this.columns.slice(i, j),
          });
          i = j;
        } else {
          segments.push({ key: col.id, merged: false, cols: [col] });
          i++;
        }
      }
      return segments;
    },
    // Uch qavatli sarlavha: guruh → ustun → chorak. Guruhsiz ustun birinchi ikki
    // qavatni `rowspan` bilan egallaydi.
    headerRows() {
      const span = this.activeQuarters.length;
      const top = [];
      const second = [];
      const third = [];
      this.headerSegments.forEach((seg) => {
        if (seg.merged) {
          top.push({
            key: "g-" + seg.key, kind: "group", label: seg.groupLabel,
            colspan: seg.cols.length * span, rowspan: 1,
          });
          seg.cols.forEach((col) => second.push({ key: "c-" + col.id, kind: "col", col, colspan: span }));
        } else {
          top.push({ key: "c-" + seg.cols[0].id, kind: "col", col: seg.cols[0], colspan: span, rowspan: 2 });
        }
      });
      if (this.isMultiQuarter) {
        this.gridColumns.forEach((gc) => third.push({
          key: "q-" + gc.key, gcKey: gc.key, col: gc.col, quarter: gc.quarter, first: gc.first,
        }));
      }
      return { top, second, third };
    },
    dirtyCount() {
      return Object.keys(this.pendingChanges).length;
    },
    // T/R har bir shoxda `order` bo'yicha qaytadan 1 dan sanaladi.
    layerNumbers() {
      return buildLayerNumbers(this.rowsTree);
    },
    visibleRows() {
      const acc = [];
      const walk = (nodes, level) => nodes.forEach((n) => {
        if (this.hideEmptyRows && !this.rowHasData(n)) return;
        acc.push({ row: n, level });
        if (n.children?.length && this.expandedIds.has(n.id)) walk(n.children, level + 1);
      });
      walk(this.rowsTree, 0);
      return acc;
    },
    focusedPreview() {
      if (!this.focusedCell) return null;
      const vr = this.visibleRows[this.focusedCell.rowIndex];
      const gc = this.gridColumns[this.focusedCell.colIndex];
      if (!vr || !gc || gc.col.value_type !== "AMOUNT") return null;
      let num;
      if (this.editingCell) {
        num = parseAmountWithUnit(this.editBuffer);
      } else {
        num = this.effectiveCell(vr.row, gc).num;
      }
      return {
        rowTitle: displayLangText(vr.row.title),
        colTitle: displayLangText(gc.col.title),
        quarterLabel: quarterLabel(gc.quarter),
        num,
        scale: gc.col.scale,
        decimals: gc.col.decimals,
        unit: scaleUnitLabel(gc.col.scale),
      };
    },
  },
  watch: {
    hideEmptyRows() {
      this.editingCell = null;
      this.focusedCell = null;
    },
    // Yilni SubsidiesIndex almashtiradi (saqlanmagan o'zgarishlar u yerda so'raladi).
    year() {
      this.resetEdits();
      this.periodView = ALL_QUARTERS;
      this.loadedView = ALL_QUARTERS;
      this.fetchMatrix();
    },
  },
  methods: {
    displayLangText,
    scaleUnitLabel,
    formatScaled,
    quarterColor,
    quarterShort(quarter) {
      return QUARTER_OPTIONS.find((q) => q.value === quarter)?.short || quarterLabel(quarter);
    },
    quarterHasData(quarter) {
      return periodHasData(this.periods, this.year, quarter);
    },
    headerTop(rowIndex) {
      return { top: (this.headerOffsets[rowIndex] || 0) + "px" };
    },
    measureHeader() {
      this.$nextTick(() => {
        const rows = this.$refs.gridWrap?.querySelectorAll("thead tr") || [];
        let acc = 0;
        const offsets = [];
        rows.forEach((tr) => {
          offsets.push(acc);
          acc += tr.getBoundingClientRect().height;
        });
        this.headerOffsets = offsets;
      });
    },
    handleApiError(error, fallbackTitle) {
      ElNotification({
        title: fallbackTitle,
        message: error?.response?.data?.message || "Iltimos, qayta urinib ko'ring.",
        type: "error",
      });
    },

    // ---------- data loading ----------
    fetchRootOptions() {
      this.$http
        .get(`${API_BASE}/rows`)
        .then(({ data }) => {
          const flat = [];
          const walk = (nodes, level) => (nodes || []).forEach((n) => {
            flat.push({ id: n.id, label: "—".repeat(level) + " " + (displayLangText(n.title) || "...") });
            if (n.children?.length) walk(n.children, level + 1);
          });
          walk(data, 0);
          this.rootOptions = flat;
        })
        .catch(() => {
        });
    },
    // Bitta so'rov = bitta chorak (§9.1), shuning uchun "barcha choraklar" ko'rinishi
    // 4 ta parallel so'rov qiladi va kataklarni chorak bo'yicha indekslaydi.
    fetchMatrix() {
      if (!this.year) return;
      const quarters = this.activeQuarters;
      this.loading = true;
      Promise.all(quarters.map((quarter) => this.$http.get(`${API_BASE}/admin/matrix`, {
        params: {
          year: this.year,
          quarter,
          search: this.search || undefined,
          root_id: this.rootId || undefined,
        },
      })))
        .then((responses) => {
          const first = responses[0]?.data || {};
          this.columns = Array.isArray(first.columns) ? first.columns : [];
          // Qatorlar daraxti chorakka bog'liq emas — birinchi javobdan olinadi.
          this.rowsTree = sortTreeByOrder(Array.isArray(first.rows) ? first.rows : []);
          const index = {};
          responses.forEach((response, i) => {
            index[quarters[i]] = this.buildCellIndex(response?.data?.rows);
          });
          this.cellIndex = index;
          this.loadedYear = this.year;
          this.loadedView = this.periodView;
          this.focusedCell = null;
          this.editingCell = null;
          const expanded = new Set();
          const collect = (nodes) => (nodes || []).forEach((n) => {
            if (n.children?.length) {
              expanded.add(n.id);
              collect(n.children);
            }
          });
          collect(this.rowsTree);
          this.expandedIds = expanded;
        })
        .catch((error) => this.handleApiError(error, "Jadvalni yuklashda xatolik"))
        .finally(() => {
          this.loading = false;
          this.measureHeader();
        });
    },
    buildCellIndex(rows) {
      const map = new Map();
      const walk = (nodes) => (nodes || []).forEach((n) => {
        const cells = new Map();
        (n.cells || []).forEach((c) => cells.set(c.column_id, { num: c.num ?? null, text: c.text ?? null }));
        map.set(n.id, cells);
        if (n.children?.length) walk(n.children);
      });
      walk(rows);
      return map;
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchMatrix(), 400);
    },
    onPeriodViewSelect(val) {
      if (val === this.loadedView) return;
      if (!this.dirtyCount) {
        this.applyPeriodView(val);
        return;
      }
      // Dropdown'ning v-model'i allaqachon yangi ko'rinishga o'tgan — qaror chiqquncha
      // eskisiga qaytaramiz, aks holda saqlash noto'g'ri davrga tegishli bo'lib qoladi.
      this.periodView = this.loadedView;
      this.confirmLeavePeriod(this.year, val)
        .then(() => this.applyPeriodView(val))
        .catch(() => {
        });
    },
    hasUnsavedChanges() {
      return this.dirtyCount > 0;
    },
    // CONSOLE.md §9.3 item 14 — davr almashtirilganda saqlanmagan o'zgarishlar
    // jimgina yo'qolmasin: saqlash / baribir almashtirish / bekor qilish.
    // Yilni SubsidiesIndex almashtiradi, shuning uchun tashqaridan ham chaqiriladi.
    confirmLeavePeriod(nextYear, nextView) {
      const target = this.viewLabel(nextYear, nextView === null ? this.loadedView : nextView);
      return new Promise((resolve, reject) => {
        ElMessageBox.confirm(
          `${this.dirtyCount} ta katak saqlanmagan. Davrni almashtirsangiz ular yo'qoladi.`,
          `${this.viewLabel(this.loadedYear, this.loadedView)} → ${target}`,
          {
            confirmButtonText: "Saqlab, almashtirish",
            cancelButtonText: "Baribir almashtirish",
            distinguishCancelAndClose: true,
            type: "warning",
          },
        )
          .then(() => this.saveAll({ then: resolve }))
          .catch((action) => {
            // "cancel" = "Baribir almashtirish", "close" = bekor qilish.
            if (action === "cancel") resolve();
            else reject(new Error("cancelled"));
          });
      });
    },
    viewLabel(year, view) {
      return view === ALL_QUARTERS ? `${year}-yil · I–IV choraklar` : periodLabel(year, view);
    },
    applyPeriodView(view) {
      this.periodView = view;
      this.resetEdits();
      this.fetchMatrix();
    },
    resetEdits() {
      this.pendingChanges = {};
      this.history = [];
      this.historyIndex = -1;
    },
    onPeriodCopied({ year, quarter }) {
      this.$emit("saved");
      if (year !== this.year) return;
      if (this.isMultiQuarter) this.fetchMatrix();
      else this.applyPeriodView(normalizeQuarter(quarter));
    },

    // ---------- header helpers ----------
    modeChipLabel(col) {
      if (col.value_type === "AMOUNT") return `so'm → ${scaleUnitLabel(col.scale)}`;
      if (col.value_type === "PERCENT") return "%";
      return "matn";
    },
    headerThClass(cell) {
      if (cell.kind === "group") return "sbs-th-group";
      return {
        "sbs-th-col": true,
        "is-inactive": cell.col.is_active === false,
        "is-col-active": this.focusedGridColumn?.col.id === cell.col.id,
      };
    },

    // ---------- tree ----------
    toggleExpand(rowId) {
      if (this.expandedIds.has(rowId)) this.expandedIds.delete(rowId);
      else this.expandedIds.add(rowId);
    },
    findRowById(id) {
      let found = null;
      const walk = (nodes) => {
        for (const n of nodes) {
          if (n.id === id) {
            found = n;
            return true;
          }
          if (n.children?.length && walk(n.children)) return true;
        }
        return false;
      };
      walk(this.rowsTree);
      return found;
    },
    // "Faqat to'ldirilganlar" uchun: qator yoki uning biror avlodida (ochiq choraklarning
    // birortasida) qiymat bormi — portaldagi `hide_empty` semantikasi.
    rowHasData(row) {
      const hasOwn = this.gridColumns.some((gc) => {
        const v = this.effectiveCell(row, gc);
        return v.num !== null && v.num !== undefined ? true : !!v.text;
      });
      if (hasOwn) return true;
      return (row.children || []).some((child) => this.rowHasData(child));
    },

    // ---------- cell values ----------
    cellKey(rowId, colId, quarter) {
      return `${rowId}:${colId}:${quarter}`;
    },
    originalCell(row, gc) {
      const found = this.cellIndex[gc.quarter]?.get(row.id)?.get(gc.col.id);
      return { num: found?.num ?? null, text: found?.text ?? null };
    },
    effectiveCell(row, gc) {
      const pending = this.pendingChanges[this.cellKey(row.id, gc.col.id, gc.quarter)];
      return pending || this.originalCell(row, gc);
    },
    cellParts(row, gc) {
      const col = gc.col;
      const v = this.effectiveCell(row, gc);
      if (col.value_type === "TEXT") {
        const isEmpty = v.text === null || v.text === undefined || v.text === "";
        return { primary: isEmpty ? "—" : v.text, secondary: "", isEmpty };
      }
      const isEmpty = v.num === null || v.num === undefined;
      if (isEmpty) return { primary: "—", secondary: "", isEmpty: true };
      if (col.value_type === "PERCENT") {
        return { primary: formatScaled(v.num, 0, col.decimals) + " %", secondary: "", isEmpty: false };
      }
      return {
        primary: groupThousands(Math.trunc(v.num)),
        secondary: formatScaled(v.num, col.scale, col.decimals) + " " + scaleUnitLabel(col.scale),
        isEmpty: false,
      };
    },
    cellClass(rIdx, cIdx, row, gc) {
      return {
        "is-focused": this.focusedCell?.rowIndex === rIdx && this.focusedCell?.colIndex === cIdx,
        "is-row-active": this.focusedCell?.rowIndex === rIdx,
        "is-col-active": this.focusedCell?.colIndex === cIdx,
        "is-dirty": !!this.pendingChanges[this.cellKey(row.id, gc.col.id, gc.quarter)],
        "is-inactive": gc.col.is_active === false,
        "is-q-start": gc.first && this.isMultiQuarter,
      };
    },
    razryadHead(num, scale) {
      return splitRazryad(num, scale).head;
    },
    razryadTail(num, scale) {
      return splitRazryad(num, scale).tail;
    },

    // ---------- focus / navigation ----------
    focusWrap() {
      this.$refs.gridWrap?.focus();
    },
    scrollCellIntoView(r, c) {
      this.$nextTick(() => {
        document.getElementById(`sbs-cell-${r}-${c}`)?.scrollIntoView({ block: "nearest", inline: "nearest" });
      });
    },
    moveFocus(rIdx, cIdx, dRow, dCol) {
      let nr = rIdx + dRow;
      let nc = cIdx + dCol;
      nr = Math.min(Math.max(nr, 0), this.visibleRows.length - 1);
      nc = Math.min(Math.max(nc, 0), this.gridColumns.length - 1);
      this.focusedCell = { rowIndex: nr, colIndex: nc };
      this.focusWrap();
      this.scrollCellIntoView(nr, nc);
    },
    moveFocusLinear(rIdx, cIdx, dir) {
      let nc = cIdx + dir;
      let nr = rIdx;
      if (nc < 0) {
        nc = this.gridColumns.length - 1;
        nr = Math.max(0, rIdx - 1);
      } else if (nc >= this.gridColumns.length) {
        nc = 0;
        nr = Math.min(this.visibleRows.length - 1, rIdx + 1);
      }
      this.focusedCell = { rowIndex: nr, colIndex: nc };
      this.focusWrap();
      this.scrollCellIntoView(nr, nc);
    },
    onCellClick(rIdx, cIdx) {
      if (this.editingCell && (this.editingCell.rowIndex !== rIdx || this.editingCell.colIndex !== cIdx)) {
        this.commitEdit(this.editingCell.rowIndex, this.editingCell.colIndex, {});
      }
      this.focusedCell = { rowIndex: rIdx, colIndex: cIdx };
      this.focusWrap();
    },
    onCellDblClick(rIdx, cIdx) {
      this.focusedCell = { rowIndex: rIdx, colIndex: cIdx };
      this.startEdit(rIdx, cIdx);
    },

    // ---------- editing ----------
    isEditing(rIdx, cIdx) {
      return this.editingCell?.rowIndex === rIdx && this.editingCell?.colIndex === cIdx;
    },
    rawNumberInputString(num) {
      return groupThousands(num.toString());
    },
    startEdit(rIdx, cIdx, initialChar = null) {
      const vr = this.visibleRows[rIdx];
      const gc = this.gridColumns[cIdx];
      if (!vr || !gc) return;
      this.editingCell = { rowIndex: rIdx, colIndex: cIdx };
      this.editError = false;
      if (initialChar !== null) {
        this.editBuffer = initialChar;
      } else {
        const v = this.effectiveCell(vr.row, gc);
        if (gc.col.value_type === "TEXT") {
          this.editBuffer = v.text || "";
        } else {
          this.editBuffer = v.num === null || v.num === undefined ? "" : this.rawNumberInputString(v.num);
        }
      }
      this.$nextTick(() => {
        const el = this.$refs.editInput;
        const input = Array.isArray(el) ? el[0] : el;
        input?.focus();
        input?.select();
      });
    },
    cancelEdit() {
      this.editingCell = null;
      this.editError = false;
      this.focusWrap();
    },
    onEditKeydown(e, rIdx, cIdx) {
      if (e.key === "Enter") {
        e.preventDefault();
        this.commitEdit(rIdx, cIdx, { move: e.shiftKey ? "up" : "down" });
      } else if (e.key === "Tab") {
        e.preventDefault();
        this.commitEdit(rIdx, cIdx, { move: e.shiftKey ? "prev" : "next" });
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.cancelEdit();
      }
    },
    commitEdit(rIdx, cIdx, opts) {
      if (!this.editingCell) return;
      const vr = this.visibleRows[rIdx];
      const gc = this.gridColumns[cIdx];
      if (!vr || !gc) {
        this.editingCell = null;
        return;
      }
      const col = gc.col;
      const raw = this.editBuffer;
      let newNum = null;
      let newText = null;
      if (col.value_type === "TEXT") {
        newText = raw.trim() === "" ? null : raw;
      } else if (raw.trim() !== "") {
        const parsed = col.value_type === "AMOUNT" ? parseAmountWithUnit(raw) : parseNumericInput(raw);
        if (parsed === null) {
          newNum = null;
        } else if (Number.isNaN(parsed)) {
          this.editError = true;
          ElMessage({ type: "warning", message: "Noto'g'ri son formati" });
          return;
        } else if (col.value_type === "AMOUNT" && Math.abs(parsed) > MAX_SAFE_AMOUNT) {
          this.editError = true;
          ElMessage({ type: "error", message: `Qiymat ${groupThousands(MAX_SAFE_AMOUNT)} so'mdan katta bo'lishi mumkin emas` });
          return;
        } else {
          newNum = parsed;
        }
      }
      this.applyCellChange(vr.row, gc, { num: newNum, text: newText }, true);
      this.editingCell = null;
      this.editError = false;
      const move = opts?.move;
      if (move === "down") this.moveFocus(rIdx, cIdx, 1, 0);
      else if (move === "up") this.moveFocus(rIdx, cIdx, -1, 0);
      else if (move === "next") this.moveFocusLinear(rIdx, cIdx, 1);
      else if (move === "prev") this.moveFocusLinear(rIdx, cIdx, -1);
      else {
        this.focusedCell = { rowIndex: rIdx, colIndex: cIdx };
        this.focusWrap();
      }
    },
    clearCell(rIdx, cIdx) {
      const vr = this.visibleRows[rIdx];
      const gc = this.gridColumns[cIdx];
      if (!vr || !gc) return;
      const cur = this.effectiveCell(vr.row, gc);
      if (cur.num === null && cur.text === null) return;
      this.applyCellChange(vr.row, gc, { num: null, text: null }, true);
    },
    applyCellChange(row, gc, newVal, pushHistory) {
      const key = this.cellKey(row.id, gc.col.id, gc.quarter);
      const original = this.originalCell(row, gc);
      const isBackToOriginal = newVal.num === original.num && newVal.text === original.text;
      if (pushHistory) {
        const prevPending = this.pendingChanges[key];
        this.history.splice(this.historyIndex + 1);
        this.history.push({
          key,
          prev: prevPending ? { ...prevPending } : { ...original, wasOriginal: true },
          next: { ...newVal },
        });
        this.historyIndex = this.history.length - 1;
      }
      if (isBackToOriginal) delete this.pendingChanges[key];
      else this.pendingChanges[key] = { num: newVal.num, text: newVal.text };
      this.maybeWarnDeviation(row, gc, newVal);
    },
    maybeWarnDeviation(row, gc, newVal) {
      if (gc.col.value_type !== "AMOUNT" || !newVal.num) return;
      const others = [];
      this.cellIndex[gc.quarter]?.forEach((cells, rowId) => {
        const cell = cells.get(gc.col.id);
        if (cell && cell.num && rowId !== row.id) others.push(Math.abs(cell.num));
      });
      if (!others.length) return;
      const avg = others.reduce((a, b) => a + b, 0) / others.length;
      const v = Math.abs(newVal.num);
      if (avg > 0 && (v / avg > 1000 || avg / v > 1000)) {
        ElMessage({ type: "warning", message: "Bu qiymat qolganlardan 1000× farq qilyapti. So'mda kiritdingizmi?", duration: 4000 });
      }
    },
    undo() {
      if (this.historyIndex < 0) return;
      const action = this.history[this.historyIndex];
      if (action.prev.wasOriginal) delete this.pendingChanges[action.key];
      else this.pendingChanges[action.key] = { num: action.prev.num, text: action.prev.text };
      this.historyIndex--;
    },
    redo() {
      if (this.historyIndex + 1 >= this.history.length) return;
      this.historyIndex++;
      const action = this.history[this.historyIndex];
      const [rowId, colId, quarter] = action.key.split(":").map(Number);
      const row = this.findRowById(rowId);
      const col = this.columns.find((c) => c.id === colId);
      if (!row || !col) return;
      const original = this.originalCell(row, { col, quarter });
      if (action.next.num === original.num && action.next.text === original.text) {
        delete this.pendingChanges[action.key];
      } else {
        this.pendingChanges[action.key] = { num: action.next.num, text: action.next.text };
      }
    },

    // ---------- keyboard / clipboard ----------
    onKeydown(e) {
      if (this.editingCell) return;
      if (!this.focusedCell) return;
      const { rowIndex, colIndex } = this.focusedCell;
      const key = e.key;
      const dirMap = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };
      if (dirMap[key]) {
        e.preventDefault();
        this.moveFocus(rowIndex, colIndex, dirMap[key][0], dirMap[key][1]);
      } else if (key === "Tab") {
        e.preventDefault();
        this.moveFocusLinear(rowIndex, colIndex, e.shiftKey ? -1 : 1);
      } else if (key === "Enter" || key === "F2") {
        e.preventDefault();
        this.startEdit(rowIndex, colIndex);
      } else if (key === "Delete" || key === "Backspace") {
        e.preventDefault();
        this.clearCell(rowIndex, colIndex);
      } else if ((e.ctrlKey || e.metaKey) && key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) this.redo();
        else this.undo();
      } else if ((e.ctrlKey || e.metaKey) && key.toLowerCase() === "s") {
        e.preventDefault();
        this.saveAll();
      } else if (key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        this.startEdit(rowIndex, colIndex, key);
      }
    },
    onCopy(e) {
      if (this.editingCell || !this.focusedCell) return;
      e.preventDefault();
      const vr = this.visibleRows[this.focusedCell.rowIndex];
      const gc = this.gridColumns[this.focusedCell.colIndex];
      if (!vr || !gc) return;
      const v = this.effectiveCell(vr.row, gc);
      const text = gc.col.value_type === "TEXT"
        ? (v.text || "")
        : (v.num === null || v.num === undefined ? "" : String(v.num));
      e.clipboardData?.setData("text/plain", text);
    },
    onPaste(e) {
      if (this.editingCell || !this.focusedCell) return;
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData)?.getData("text/plain");
      if (!text) return;
      const lines = text.replace(/\r/g, "").split("\n");
      while (lines.length && lines[lines.length - 1] === "") lines.pop();
      const startR = this.focusedCell.rowIndex;
      const startC = this.focusedCell.colIndex;
      let applied = 0;
      let outOfRange = 0;
      const changes = [];
      lines.forEach((lineText, ri) => {
        lineText.split("\t").forEach((cellText, ci) => {
          const r = startR + ri;
          const c = startC + ci;
          if (r >= this.visibleRows.length || c >= this.gridColumns.length) {
            outOfRange++;
            return;
          }
          const row = this.visibleRows[r].row;
          const gc = this.gridColumns[c];
          const trimmed = cellText.trim();
          let num = null;
          let txt = null;
          if (gc.col.value_type === "TEXT") {
            txt = trimmed === "" ? null : trimmed;
          } else if (trimmed !== "") {
            const parsed = gc.col.value_type === "AMOUNT" ? parseAmountWithUnit(trimmed) : parseNumericInput(trimmed);
            if (parsed === null || Number.isNaN(parsed)
              || (gc.col.value_type === "AMOUNT" && Math.abs(parsed) > MAX_SAFE_AMOUNT)) {
              outOfRange++;
              return;
            }
            num = parsed;
          }
          changes.push({ row, gc, num, text: txt });
          applied++;
        });
      });
      if (!applied) {
        ElMessage({ type: "warning", message: "Qo'yish uchun mos ma'lumot topilmadi" });
        return;
      }
      const msg = outOfRange > 0
        ? `${applied} ta ma'lumot qo'yiladi, ${outOfRange} tasi jadval chegarasidan yoki noto'g'ri formatdan tashqarida qoldi. Davom etasizmi?`
        : `${applied} ta ma'lumot qo'yiladi. Davom etasizmi?`;
      ElMessageBox.confirm(msg, "Excel'dan qo'yish", {
        confirmButtonText: "Ha, qo'yish",
        cancelButtonText: "Bekor qilish",
        type: "warning",
      })
        .then(() => {
          changes.forEach((ch) => this.applyCellChange(ch.row, ch.gc, { num: ch.num, text: ch.text }, true));
          ElMessage({ type: "success", message: `${applied} ta ma'lumot qo'yildi` });
        })
        .catch(() => {
        });
    },

    // ---------- save ----------
    saveAll(opts) {
      if (!this.dirtyCount || this.saving) {
        opts?.then?.();
        return;
      }
      // Bitta so'rov = bitta (qator, chorak) juftligi — §9.6.
      const groups = {};
      Object.entries(this.pendingChanges).forEach(([key, val]) => {
        const [rowId, colId, quarter] = key.split(":").map(Number);
        const groupKey = `${rowId}:${quarter}`;
        if (!groups[groupKey]) groups[groupKey] = { rowId, quarter, values: [] };
        groups[groupKey].values.push({ column_id: colId, num: val.num, text: val.text });
      });
      const list = Object.values(groups);
      this.saving = true;
      this.saveProgress = { done: 0, total: list.length };
      // Qiymatlar aynan ekranda turgan yilga yoziladi — yil oraliqda o'zgarsa ham
      // ma'lumot boshqa yilga tushmaydi.
      this.saveSequential(list, 0, this.loadedYear, opts);
    },
    saveSequential(list, idx, targetYear, opts) {
      if (idx >= list.length) {
        this.saving = false;
        ElMessage({ type: "success", message: `${targetYear}-yil uchun barcha o'zgarishlar saqlandi` });
        this.$emit("saved");
        if (opts?.then) opts.then();
        else this.fetchMatrix();
        return;
      }
      const { rowId, quarter, values } = list[idx];
      this.$http
        // CONSOLE.md §9.6 — `quarter` yuborilmasa server yillikka yozadi.
        .put(`${API_BASE}/rows/${rowId}/values`, { year: targetYear, quarter, replace: false, values })
        .then(() => {
          values.forEach((v) => delete this.pendingChanges[this.cellKey(rowId, v.column_id, quarter)]);
          this.saveProgress.done++;
          this.saveSequential(list, idx + 1, targetYear, opts);
        })
        .catch((error) => {
          this.saving = false;
          ElNotification({
            title: `Qator #${rowId} (${quarterLabel(quarter)}) saqlanmadi`,
            message: error?.response?.data?.message || "Iltimos, qayta urinib ko'ring.",
            type: "error",
          });
        });
    },

    // ---------- export ----------
    exportCsv() {
      const esc = (v) => {
        const s = String(v ?? "");
        return /[;"\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      const lines = [];
      lines.push(`Subsidiyalar — ${this.viewLabel(this.loadedYear, this.loadedView)}`);
      const header = ["№", "Ko'rsatkich", ...this.gridColumns.map((gc) => {
        const group = displayLangText(gc.col.group);
        const title = (group ? group + " — " : "") + displayLangText(gc.col.title);
        return this.isMultiQuarter ? `${title} (${quarterLabel(gc.quarter)})` : title;
      })];
      lines.push(header.map(esc).join(";"));
      this.visibleRows.forEach((vr) => {
        const row = vr.row;
        const cells = this.gridColumns.map((gc) => {
          const v = this.effectiveCell(row, gc);
          if (gc.col.value_type === "TEXT") return v.text || "";
          return v.num === null || v.num === undefined ? "" : String(v.num);
        });
        lines.push([this.layerNumbers.get(row.id), displayLangText(row.title), ...cells].map(esc).join(";"));
      });
      const csv = "﻿" + lines.join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = this.isMultiQuarter
        ? `subsidiyalar_${this.loadedYear}_choraklar.csv`
        : `subsidiyalar_${periodFileSuffix(this.loadedYear, this.loadedView)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    // "Ko'rsatkich" ustuni kengligini sudrab o'zgartirish (Excel'dagidek).
    startTitleResize(event) {
      const startX = event.clientX;
      const startWidth = this.titleWidth;
      const prevCursor = document.body.style.cursor;
      const prevSelect = document.body.style.userSelect;
      const onMove = (e) => {
        const next = Math.round(startWidth + e.clientX - startX);
        this.titleWidth = Math.min(TITLE_WIDTH_MAX, Math.max(TITLE_WIDTH_MIN, next));
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevSelect;
        this.titleResizeCleanup = null;
        this.persistTitleWidth();
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      this.titleResizeCleanup = onUp;
    },
    resetTitleWidth() {
      this.titleWidth = TITLE_WIDTH_DEFAULT;
      this.persistTitleWidth();
    },
    persistTitleWidth() {
      try {
        localStorage.setItem(TITLE_WIDTH_KEY, String(this.titleWidth));
      } catch (e) {
        // saqlanmasa ham joriy sessiyada kenglik ishlaydi
      }
    },

    rowTrClass(vr, rIdx) {
      return {
        "sbs-row--inactive": vr.row.is_active === false,
        "sbs-row--focused": this.focusedCell?.rowIndex === rIdx,
      };
    },
  },
  mounted() {
    this.fetchMatrix();
    this.fetchRootOptions();
  },
  beforeUnmount() {
    if (this.titleResizeCleanup) this.titleResizeCleanup();
  },
};
</script>

<style scoped lang="scss">
$brand: #007791;
$navy: #0a194e;
$border: #ebeef5;
$muted: #8a94a6;

// Nom ustunining amaldagi kengligi: foydalanuvchi tanlagani, tor ekranda esa
// raqam ustunlariga joy qolishi uchun cheklangani.
.sbs-matrix {
  --sbs-title-eff: var(--sbs-title-w, 320px);

  @media (max-width: 900px) {
    --sbs-title-eff: min(var(--sbs-title-w, 320px), 46vw);
  }
}

.sbs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  &__filters,
  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
}

.sbs-save-progress {
  font-size: 12px;
  color: $muted;
}

.sbs-hide-empty {
  margin-left: 2px;
}

// Ustunsiz yil uchun qadamli ko'rsatma (yil → ko'rsatkichlar → ustunlar → qiymatlar).
.sbs-setup {
  padding: 28px 26px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #fbfcfe;

  &__title {
    margin: 0 0 6px;
    font-family: "Montserrat-Bold", sans-serif;
    font-size: 16px;
    color: $navy;
  }

  &__text {
    margin: 0 0 14px;
    font-size: 13px;
    color: $muted;
  }

  &__steps {
    margin: 0;
    padding-left: 20px;
    font-size: 13px;
    color: #303133;

    li {
      margin-bottom: 10px;
      line-height: 1.7;
    }

    .el-button {
      margin-left: 8px;
    }
  }
}

// Joriy davr banneri (CONSOLE.md §9.3 item 13/15).
.sbs-period-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
  padding: 9px 14px;
  border-radius: 10px;
  border-left: 4px solid var(--sbs-q-color, #64748b);
  background: #f6f9fc;

  &__label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: $muted;
  }

  &__value {
    font-family: "Montserrat-Bold", sans-serif;
    font-size: 15px;
    color: var(--sbs-q-color, #64748b);
  }

  &__note {
    font-size: 12px;
    color: $muted;
  }

  &__dirty {
    margin-left: auto;
    padding: 2px 10px;
    border-radius: 20px;
    background: rgba(249, 115, 22, 0.14);
    color: #c2410c;
    font-size: 12px;
    font-weight: 600;
  }
}

.sbs-period-option {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.sbs-period-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #dfe3ea;

  &.is-on {
    background: $brand;
  }
}

.sbs-grid-wrap {
  max-height: 68vh;
  overflow: auto;
  border: 1px solid $border;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 30, 80, 0.04);
  outline: none;
}

.sbs-grid {
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
  min-width: 100%;
  font-size: 12.5px;
}

.sbs-grid th,
.sbs-grid td {
  border-right: 1px solid $border;
  border-bottom: 1px solid $border;
  white-space: nowrap;
}

.sbs-grid thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #f6f9fc;
  color: $navy;
  font-family: "Montserrat-Medium", sans-serif;
  font-weight: 600;
  padding: 8px 10px;
  text-align: left;

  &.is-inactive {
    background: #eef0f3;
    color: $muted;
  }

  &.is-col-active {
    background: rgba(0, 119, 145, 0.09);
  }
}

.sbs-th-group {
  text-align: center;
}

// Chorak qavati — sarlavhaning uchinchi qatori, har chorak o'z rangida.
.sbs-th-quarter {
  z-index: 3;
  text-align: center;
  font-size: 11.5px;
  color: var(--sbs-q-color, $navy);
  border-bottom: 2px solid var(--sbs-q-color, $border);

  &.is-q-start {
    border-left: 1px solid #d6dde6;
  }
}

.sbs-th-q-dot {
  display: inline-block;
  width: 5px;
  height: 5px;
  margin-left: 4px;
  border-radius: 50%;
  vertical-align: middle;
  background: var(--sbs-q-color, $brand);
}

.sbs-c-idx {
  position: sticky;
  left: 0;
  width: 44px;
  min-width: 44px;
}

.sbs-c-title {
  position: sticky;
  left: 44px;
  width: var(--sbs-title-eff);
  min-width: var(--sbs-title-eff);
  max-width: var(--sbs-title-eff);
  white-space: normal;
}

// Nom matni katakni cho'zib yubormasligi uchun aniq kenglikdagi blok ichida
// 2 qatorga qisqartiriladi; to'liq matn tooltipda ko'rinadi.
.sbs-title-clamp {
  box-sizing: border-box;
  width: calc(var(--sbs-title-eff) - 20px);
  // `.sbs-grid th, td` dagi nowrap spetsifikligi bo'yicha kuchliroq — shuning uchun
  // o'rash aynan shu blokda qayta yoqiladi, aks holda nom bitta qatorga cho'ziladi.
  white-space: normal;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.35;
}

.sbs-title-resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  user-select: none;

  &:hover {
    background: rgba(0, 119, 145, 0.3);
  }
}

thead .sbs-c-idx,
thead .sbs-c-title {
  z-index: 6;
}

.sbs-th-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.sbs-mode-chip {
  display: inline-flex;
  align-self: flex-start;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
  background: rgba(0, 119, 145, 0.12);
  color: $brand;

  &.sbs-chip--percent {
    background: rgba(22, 163, 74, 0.12);
    color: #16a34a;
  }

  &.sbs-chip--text {
    background: rgba(148, 163, 184, 0.2);
    color: #64748b;
  }
}

.sbs-inactive-ico {
  margin-left: 4px;
}

.sbs-grid tbody td {
  padding: 6px 10px;
  vertical-align: top;
  background: #fff;
}

.sbs-grid tbody td.sbs-c-idx,
.sbs-grid tbody td.sbs-c-title {
  position: sticky;
  z-index: 2;
  background: #fff;
}

tr.sbs-row--inactive td {
  opacity: 0.55;
}

tr.sbs-row--focused td.sbs-c-idx,
tr.sbs-row--focused td.sbs-c-title {
  background: rgba(0, 119, 145, 0.05);
}

.sbs-td-idx {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 11px;
  color: $muted;
}

.sbs-expand {
  cursor: pointer;
  user-select: none;
  width: 12px;
  display: inline-block;
  color: $brand;
}

.sbs-td-title {
  white-space: normal;
  color: #303133;

  .is-bold {
    font-weight: 700;
  }
}

.sbs-row-note {
  color: $brand;
  margin-left: 2px;
}

.sbs-td-cell {
  cursor: cell;
  position: relative;
  min-width: 108px;

  &.is-q-start {
    border-left: 1px solid #d6dde6;
  }

  &.is-focused {
    outline: 2px solid $brand;
    outline-offset: -2px;
    z-index: 1;
  }

  &.is-row-active,
  &.is-col-active {
    background: rgba(0, 119, 145, 0.045);
  }

  &.is-inactive {
    background: #f5f6f8;
  }

  &.is-dirty::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    border-style: solid;
    border-width: 7px 7px 0 0;
    border-color: #f97316 transparent transparent transparent;
  }
}

.sbs-cell-value {
  display: flex;
  flex-direction: column;
}

.sbs-cell-primary {
  color: #1e293b;
  font-weight: 500;

  &.is-empty {
    color: #cbd5e1;
    font-weight: 400;
  }
}

.sbs-cell-secondary {
  font-size: 10.5px;
  color: $muted;
  margin-top: 1px;
}

.sbs-cell-input {
  width: 100%;
  border: none;
  outline: 2px solid $brand;
  border-radius: 3px;
  padding: 2px 4px;
  font-size: 12.5px;
  font-family: inherit;

  &.is-error {
    outline-color: #e11d48;
    background: rgba(225, 29, 72, 0.06);
  }
}

.sbs-empty-row {
  text-align: center;
  color: $muted;
  padding: 20px;
}

.sbs-preview-bar {
  margin-top: 10px;
  padding: 8px 14px;
  background: rgba(0, 119, 145, 0.06);
  border-radius: 8px;
  font-size: 12.5px;
  color: #005c72;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.sbs-preview-label {
  color: $muted;
  font-weight: 500;
}

.sbs-preview-raw {
  font-family: "SFMono-Regular", Consolas, monospace;
}

.sbs-preview-tail {
  color: #b3b9c4;
}

.sbs-preview-arrow {
  color: $muted;
}

.sbs-preview-scaled {
  font-weight: 700;
}

.sbs-muted {
  color: $muted;
}

.sbs-kbd-hint {
  margin: 8px 2px 0;
  font-size: 11px;
  color: $muted;
}
</style>
